import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Download, ChevronRight, ArrowLeft, Loader2 } from 'lucide-react'
import { Button } from 'components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { initializeTemplateSiteDetails } from 'slices/templateSlice'
import notify from 'components/ui/notify'

interface Template {
	_id: string
	templateName: string
	description?: string
	accessLevel?: string
	createdBy?: { name?: string }
}

interface TemplateSiteDetail {
	_id: string
	detailLabel: string
	description?: string
	detailType: string
}

interface SiteDetailTemplatesSelectCNProps {
	detailTemplates: Template[]
	handleTemplateLoad: (templateId: string, resetForm?: () => void) => void
}

const SiteDetailTemplatesSelectCN = ({
	detailTemplates,
	handleTemplateLoad,
}: SiteDetailTemplatesSelectCNProps) => {
	const dispatch = useDispatch()
	const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)

	const {
		entities: detailEntities,
		ids: detailIds,
		isLoading: detailsLoading,
	} = useSelector((state: any) => state.templates.siteDetails)

	const templateDetails: TemplateSiteDetail[] = (detailIds || [])
		.map((id: string) => detailEntities[id])
		.filter(Boolean)
		.sort((a: any, b: any) => (a.seq || 0) - (b.seq || 0))

	const handleTemplateClick = (template: Template) => {
		setSelectedTemplate(template)
		dispatch(initializeTemplateSiteDetails(template._id) as any)
	}

	const handleBack = () => {
		setSelectedTemplate(null)
	}

	const handleDownload = (template: Template) => {
		notify({
			text: `This will download the ${template.templateName} template to your Site Details list.`,
			buttons: ['Cancel', 'Ok!'],
		}).then(async value => {
			if (value) {
				await handleTemplateLoad(template._id)
				notify(
					'Downloaded!',
					`The ${template.templateName} template has been downloaded to your Site Details list.`,
					'success',
				)
			}
		})
	}

	// ─── Detail Preview View ────────────────────────────────────────────────────

	if (selectedTemplate) {
		return (
			<div className="w-full flex flex-col gap-3 p-3">
				{/* Header with back button */}
				<div className="flex items-center gap-3">
					<button
						type="button"
						onClick={handleBack}
						className="bg-transparent border-none p-1 rounded-md hover:bg-gray-100 cursor-pointer text-gray-500 hover:text-gray-700 transition-colors"
					>
						<ArrowLeft className="w-4 h-4" />
					</button>
					<div className="flex-1 min-w-0">
						<h3 className="text-sm font-semibold text-gray-900 m-0 truncate">
							{selectedTemplate.templateName}
						</h3>
						{selectedTemplate.description && (
							<p className="text-xs text-gray-500 m-0 mt-0.5 truncate">
								{selectedTemplate.description}
							</p>
						)}
					</div>
					<Button onClick={() => handleDownload(selectedTemplate)} className="shrink-0">
						<Download className="w-3.5 h-3.5 mr-1.5" />
						Download
					</Button>
				</div>

				{/* Detail list */}
				<div className="border border-gray-200 rounded-lg overflow-hidden">
					{detailsLoading ? (
						<div className="flex items-center justify-center py-8 text-gray-400">
							<Loader2 className="w-5 h-5 animate-spin" />
						</div>
					) : templateDetails.length === 0 ? (
						<div className="py-6 text-center text-sm text-gray-500">
							No details in this template
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow className="bg-gray-50 hover:bg-gray-50">
									<TableHead className="text-xs">Label</TableHead>
									<TableHead className="w-24 text-xs">Type</TableHead>
									<TableHead className="text-xs">Note</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{templateDetails.map(detail => (
									<TableRow key={detail._id} className="h-8">
										<TableCell className="py-1 text-sm text-gray-900">
											{detail.detailLabel}
										</TableCell>
										<TableCell className="py-1">
											<span className="inline-block text-xs px-1.5 py-0.5 rounded font-medium text-[#0080fe] bg-[#0080fe]/10 border border-[#0080fe]/20">
												{detail.detailType}
											</span>
										</TableCell>
										<TableCell className="py-1 text-xs text-gray-500">
											{detail.description || '—'}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>

				<div className="text-xs text-gray-400 text-center">
					{templateDetails.length} detail{templateDetails.length !== 1 ? 's' : ''} in this
					template
				</div>
			</div>
		)
	}

	// ─── Template List View ─────────────────────────────────────────────────────

	return (
		<div className="w-full p-3">
			{detailTemplates.length === 0 ? (
				<div className="py-8 text-center text-sm text-gray-500">
					No site detail templates available
				</div>
			) : (
				<div className="border border-gray-200 rounded-lg overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-gray-50 hover:bg-gray-50">
								<TableHead className="text-xs">Template</TableHead>
								<TableHead className="text-xs">Description</TableHead>
								<TableHead className="w-20 text-xs text-center">Download</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{detailTemplates.map(template => (
								<TableRow key={template._id} className="h-9 hover:bg-gray-50">
									<TableCell
										className="py-1.5 cursor-pointer"
										onClick={() => handleTemplateClick(template)}
									>
										<div className="flex items-center gap-1.5">
											<span className="text-sm text-gray-900 hover:text-blue-600 transition-colors">
												{template.templateName}
											</span>
											<ChevronRight className="w-3 h-3 text-gray-400" />
										</div>
									</TableCell>
									<TableCell
										className="py-1.5 text-sm text-gray-500 cursor-pointer"
										onClick={() => handleTemplateClick(template)}
									>
										{template.description || '—'}
									</TableCell>
									<TableCell className="py-1.5 text-center">
										<button
											type="button"
											className="inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-blue-50 transition-colors text-blue-600 bg-transparent border-none cursor-pointer"
											onClick={() => handleDownload(template)}
											title="Download template"
										>
											<Download className="h-4 w-4" />
										</button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	)
}

export default SiteDetailTemplatesSelectCN
